import React, { useContext, useState, useEffect } from "react";
import classNames from "classnames";
import { DataSourceType, IDataSource, IDataSourceBrowserProps, IDataSourceMetadata, IFilters, ISourceOrigins } from "@types";
import { QueryBuilderContext } from "@query-builder/QueryBuilderContext";
import { getAvailableSources, getAvailableSourcesForHistoricalData, resetConfig } from "@query-engine-service";
import { AsyncButton, Button, hasValue, Modal, SearchBox, Select, useDebounce } from "uxp/components";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSourceMetadataFromId, textSearch, prepareSources, SourceOriginSQL } from "@utils";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { CustomIconButton } from "@components/CustomIconButton/CustomIconButton";
import { Loading } from "@components/Loading/Loading";
import './DataSourceBrowser.scss';
import { isSourceConfigurable, ParamEditorModal } from "@components/QueryEditor/ParamEditor";

export function renderIcon(type: DataSourceType) {
    let iconType = null;
    switch (type) {
        case DataSourceType.SQL:
            iconType = 'database';
            break;
        case DataSourceType.DX:
            iconType = 'database';
            break;
        case DataSourceType.COLLECTION:
            iconType = 'server';
            break;
        case DataSourceType.JSON:
            iconType = 'file-alt';
            break;
        case DataSourceType.WORKFLOW:
            iconType = 'sitemap';
            break;
        case DataSourceType.Query:
            iconType = 'save';
            break;
    }

    if (!hasValue(iconType)) return null
    return <FontAwesomeIcon icon={['fas', iconType as IconName]} />;
}

export const DataSourceBrowser: React.FunctionComponent<IDataSourceBrowserProps> = (props) => {

    const { source: selectedSource, onSelect, showCloseButton, onClose, history, configuredSources, query } = props
    const context = useContext(QueryBuilderContext)

    const [datasources, setDatasources] = useState<IDataSource[]>([])
    const [filteredSources, setFilteredSources] = useState<IDataSource[]>([])
    const [searchText, setSearchText] = useState('')
    const [filters, setFilters] = useState<IFilters>({ type: '__all__', origin: '__all__' })
    const [sourceOrigins, setSourceOrigins] = useState<ISourceOrigins>({})
    const [loading, setLoading] = useState(true)
    const [filtering, setFiltering] = useState(true)
    const [editInstance, setEditinstance] = useState<IDataSource | null>(null)

    const searchQuery = useDebounce(searchText, 300)

    useEffect(() => {
        getAllDataSources()
    }, [context]);

    useEffect(() => {

        setFiltering(true)
        const filtered = (datasources || []).filter(source => {
            const matchType = (!hasValue(filters?.type) || filters?.type == '__all__') ? true : source.type == filters.type
            const matchOrigin = (!hasValue(filters?.origin) || filters?.origin == '__all__') ? true : source.origin == filters?.origin
            const matchQuery = hasValue(searchQuery) ? (textSearch(source.name, searchQuery, true) || textSearch(source.id, searchQuery, true)) : true
            const ignoreCurrentEditingQuery = (source.type == DataSourceType.Query ? source.queryId !== context?.editingQueryId : true)

            return matchType && matchOrigin && matchQuery && ignoreCurrentEditingQuery
        });

        setFilteredSources(filtered);
        setFiltering(false)

    }, [datasources, searchQuery, filters])

    async function getAllDataSources() {
        setLoading(true)
        const result = await (history ? getAvailableSourcesForHistoricalData : getAvailableSources)?.(context);
        console.log('__source_before', result?.definitions)

        const { sources, origins } = prepareSources(result?.definitions || [], props?.origins || [])
        console.log('__source_after', sources, origins)
        setDatasources(sources);
        setSourceOrigins(origins)
        setLoading(false)
    }

    function renderMetadata(metadata: IDataSourceMetadata | null) {
        if (!metadata) return null;

        return (
            <div className="source-metadata-container">
                {hasValue(metadata?.model) &&
                    <div className="metadata-section">
                        <div className="metadata-section-label">Model</div>
                        <div className="metadata-section-value">{metadata.model}</div>
                    </div>
                }
                {hasValue(metadata?.collection) &&
                    <div className="metadata-section">
                        <div className="metadata-section-label">Collection</div>
                        <div className="metadata-section-value">{metadata.collection}</div>
                    </div>
                }
                {hasValue(metadata?.table) &&
                    <div className="metadata-section">
                        <div className="metadata-section-label">Table</div>
                        <div className="metadata-section-value">{metadata.table}</div>
                    </div>
                }
                {hasValue(metadata?.action) &&
                    <div className="metadata-section">
                        <div className="metadata-section-label">Action</div>
                        <div className="metadata-section-value">{metadata.action}</div>
                    </div>
                }
                {hasValue(metadata?.queryName) &&
                    <div className="metadata-section">
                        <div className="metadata-section-label">Query</div>
                        <div className="metadata-section-value">{metadata.queryName}</div>
                    </div>
                }
            </div>
        )
    }

    function handleSelect(source: IDataSource) {
        const { isConfigurable } = isSourceConfigurable(source)
        if (isConfigurable) {

            const configuredSource = (configuredSources || []).find(s => s.id == source.id)
            if (!!configuredSource) {
                source.params = { ...source.params, ...configuredSource?.params || {} }
            }

            setEditinstance(source)
            return
        }
        onSelect(source);
    }

    function renderSource(source: IDataSource, key: number) {

        const isSelected = source?.id == selectedSource?.id
        const { type, metadata } = getSourceMetadataFromId(source?.id)
        if (!type) return null

        return (
            <div className={classNames("data-source-browser-source-container", { 'selected': isSelected })} key={key}>
                <div className="data-source-browser-source-contianer-head">
                    <div className="source-icon-container">{renderIcon(type)}</div>
                    <div className="source-label"> {source.name}</div>
                </div>
                <div className="data-source-browser-source-contianer-body">
                    {renderMetadata(metadata)}
                    {/* <div className="source-description"> {source.description || ''}</div> */}
                </div>
                <div className="data-source-browser-source-contianer-footer">
                    <Button title="Select" onClick={() => handleSelect(source)} icon="fas check-circle" />
                </div>
            </div>
        )
    }

    function renderOrigins() {
        const _origins = (Object.keys(sourceOrigins) || []).filter(o => {
            const _type = filters?.type || '__all__'
            const _types = sourceOrigins[o].types || []

            if (_type == '__all__') return true
            if (_type == DataSourceType.SQL) return o == SourceOriginSQL
            // if (_type == DataSourceType.DX) return o == SourceOriginSQL
            return _types.includes(_type)
        })
        _origins.sort()

        return <div className="data-source-browser-filter-origins">
            <div className={classNames("data-source-browser-filter-origin", { 'selected': filters.origin == '__all__' })} onClick={() => { setFilters(prev => ({ ...prev, origin: '__all__' })) }}  >All </div>
            {_origins.map((o, i) => {
                return <div className={classNames("data-source-browser-filter-origin", { 'selected': filters.origin == o })} onClick={() => { setFilters(prev => ({ ...prev, origin: o })) }}  >{o}</div>
            })}
        </div>
    }

    async function refreshDatasource() {
        try {
            await resetConfig(context)
            getAllDataSources()
        }
        catch (e) { }
    }

    return <div className="lucy-query-editor-data-source-browser-component-container">
        <div className="data-source-browser-head">

            <div className="data-source-browser-head-title">Select a datasource</div>

            <div className="data-source-browser-head-actions">

                <SearchBox
                    value={searchText}
                    onChange={setSearchText}
                />

                <div style={{ padding: '5px 10px' }}>

                    <AsyncButton
                        title="Refresh"
                        onClick={refreshDatasource}
                        loadingTitle="Refreshing..."
                        icon="fas sync-alt"
                    />

                </div>

                {showCloseButton ?
                    <CustomIconButton
                        icon={['fas', 'times']}
                        onClick={() => { onClose?.() }}
                    />
                    : null}


            </div>
        </div>

        <div className="data-source-browser-body">
            <div className="data-source-browser-filter-container">
                <div className="data-source-browser-filter-content">

                    <div className="data-source-browser-filter-header">Filter Sources by</div>

                    <div className="data-source-browser-filter">
                        <div className="data-source-browser-filter-label">Category</div>

                        <Select
                            selected={filters.type || '__all__'}
                            onChange={v => { setFilters(prev => ({ ...prev, type: v as any })) }}
                            options={[
                                { label: 'All', value: '__all__' },
                                ...Object.entries(DataSourceType)
                                    .filter(t => (props?.origins?.length > 0 ? props?.origins?.includes(t[1] as any) : true))
                                    .map(t => ({ label: t[0], value: t[1] }))
                            ]}
                            className="lucy-query-engine-source-filter-input"
                            dropdownClassname="lucy-query-engine-source-filter-input-dropdown"
                        />


                    </div>

                    <div className="data-source-browser-filter">
                        <div className="data-source-browser-filter-label">Location</div>
                        {(loading)
                            ? <Loading />
                            : renderOrigins()
                        }
                    </div>
                </div>
            </div>

            <div className="data-source-browser-sources-container">
                {
                    (loading || filtering)
                        ? <Loading />
                        : <>
                            {filteredSources.map((source, key) => renderSource(source, key))}
                        </>
                }
            </div>
        </div>

        <ParamEditorModal
            show={!!editInstance}
            onClose={() => setEditinstance(null)}
            source={editInstance}
            onSave={(updatedSource) => {
                onSelect(updatedSource);
                setEditinstance(null)
            }}
            query={query}
        />

    </div>
}